import { useEffect, useState } from "react";
import { styled } from "@pigment-css/react";
import { ChevronLeftOutline } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { Pill } from "@acto/taskpane/field-sales-2/components/primitives/Pill";
import { useCorporateGroupSummary } from "@acto/taskpane/field-sales-2/data-access/useCorporateGroupSummary";
import { glassCapsulePress } from "@acto/taskpane/field-sales-2/styles/glassCapsulePress";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";

const GROUP_RETURN_LABEL_FADE_Y = 24;

// Single morphing surface (the MorphPill technique): the pill animates one bounded max-width and
// clips its fixed-geometry content, so the icon never moves and no padding/gap/width reflows
// mid-flight. Collapsed it clamps to the 48px icon slot, i.e. a circle.
const GroupReturnPill = styled(Pill)(({ theme }) => ({
  gap: 0,
  padding: 0,
  minWidth: 0,
  margin: 0,
  overflow: "hidden",
  // Bounded expanded ceiling (max-width: none cannot transition): icon slot + the label's 40vw cap.
  maxWidth: `calc(${theme.fieldSalesTwo.spacing[48]} + 40vw)`,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  cursor: "pointer",
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  "& svg": {
    width: 20,
    height: 20,
    flexShrink: 0,
    color: theme.fieldSalesTwo.colors.neutral[900],
  },
  '&[data-collapsed="true"]': {
    maxWidth: theme.fieldSalesTwo.spacing[48],
  },
  ...glassCapsulePress(theme, { restShadow: theme.fieldSalesTwo.shadows.glass }),
  transition: `transform 120ms ease, box-shadow 120ms ease, max-width ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  // Repeats glassCapsulePress's transform overrides: declaring this key after the spread replaces
  // its block outright, so dropping them would leave the pill lifting under reduced motion.
  "@media (prefers-reduced-motion: reduce)": {
    transition: "transform 120ms ease, box-shadow 120ms ease",
    "&:hover": { transform: "none" },
    "&:active": { transform: "none" },
  },
}));

// Fixed 48px box the collapsed pill clamps to; keeps the chevron centered in both states.
const GroupReturnIconSlot = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "stretch",
  width: theme.fieldSalesTwo.spacing[48],
  flexShrink: 0,
}));

const GroupReturnLabel = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "40vw",
  paddingRight: theme.fieldSalesTwo.spacing[20],
  // Keeps its natural width during the morph so the pill clips it instead of re-ellipsizing per frame.
  flexShrink: 0,
  transition: `opacity ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  '&[data-hidden="true"]': {
    opacity: 0,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
}));

/**
 * Back-to-group pill labeled with the group's name (generic copy when it has none). Collapses to
 * a round icon button once the page scrolls.
 */
export function GroupReturnButton({ to, clientId }: { to: string; clientId: string | undefined }) {
  const navigate = useDirectionalNavigate();
  const t = useTranslations("fieldSalesTwo.corporateGroup");
  const { group } = useCorporateGroupSummary(clientId);

  const [isLabelHidden, setIsLabelHidden] = useState(false);
  useEffect(() => {
    const onScroll = () => setIsLabelHidden(window.scrollY > GROUP_RETURN_LABEL_FADE_Y);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const label = group?.name ?? t("backToGroup");

  return (
    <GroupReturnPill
      as="button"
      type="button"
      variant="action"
      data-collapsed={isLabelHidden}
      aria-label={t("backToGroup")}
      onClick={() => navigate(to, { direction: "pop" })}
    >
      <GroupReturnIconSlot>
        <ChevronLeftOutline aria-hidden="true" />
      </GroupReturnIconSlot>
      <GroupReturnLabel data-hidden={isLabelHidden} aria-hidden={isLabelHidden}>
        {label}
      </GroupReturnLabel>
    </GroupReturnPill>
  );
}
